import { Suspense } from "react";
import { usePlanStatus } from "../subscriptions/plan-status";

import {
	EntityFeatureGate,
	type EntityFeatureGateProps,
} from "./entity-feature-gate";

import {
	SessionFeatureGate,
	type SessionFeatureGateProps,
} from "./session-feature-gate";

type FeatureGateProps = (SessionFeatureGateProps | EntityFeatureGateProps) & {
	suspenseComponent?: React.ReactNode;
};

/**
 * FeatureGate
 *
 * A simple component you can use to "gate" off certain parts of the UI. There are two types of gating:
 *
 * - **Session Feature Gate:** Checks if a feature is generally available to the current user based on their active subscription plan. It's for features that are either "on" or "off" for the whole user session.
 * - **Entity Feature Gate:** Checks if a feature is available for a specific item (like a document or board), often considering usage limits or quotas related to that item. It's used when access depends not just on the user's plan, but also on how much they've used the feature with that specific item.
 *
 * It's very flexible in how you want to use it:
 *
 * @example
 * // Basic Usage (Show UI only for paid users)
 * <FeatureGate>
 *   This will only be shown to paid users!
 * </FeatureGate>
 *
 * @example
 * // Gating For Specific Features
 * <FeatureGate feature={SupportedFeature.DownloadAsMd}>
 *   <button>Download as Markdown</button>
 * </FeatureGate>
 *
 * @example
 * // Fallback UI
 * <FeatureGate
 *   feature={SupportedFeature.DownloadAsMd}
 *   fallbackComponent={<UpgradeNowButton />}
 * >
 *   <DownloadAsMd />
 * </FeatureGate>
 *
 * @example
 * // Alternative UI (Render props)
 * <FeatureGate
 *   feature={SupportedFeature.DownloadAsMd}
 *   render={({ enabled }) => <button disabled={!enabled}>Download</button>}
 * />
 *
 * @example
 * // Metered usage information (Render props)
 * <FeatureGate
 *   feature={SupportedFeature.DownloadAsMd}
 *   render={({ enabled, stats }) => {
 *     // Stats might not always be available. For example on unlimited plans
 *     // there is no quota and so stats will be undefined
 *     if (stats) {
 *       const { quota, current, available } = stats;
 *
 *       return (
 *         <>
 *           <span>current: {current}</span>
 *           <span>max: {quota}</span>
 *           <span>available: {available ? "limit not reached" : "limit reached"}</span>
 *         </>
 *       );
 *     }
 *
 *     return enabled ? "Unlimited power!" : "No plan :(";
 *   }}
 * />
 */
export const FeatureGate = (props: FeatureGateProps) => {
	usePlanStatus();

	if ("entity" in props) {
		return (
			<Suspense fallback={props.suspenseComponent ?? null}>
				<EntityFeatureGate {...props} />
			</Suspense>
		);
	}

	return (
		<Suspense fallback={props.suspenseComponent ?? null}>
			<SessionFeatureGate {...props} />
		</Suspense>
	);
};
